import React, { FormEvent, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { useCreateReward } from '~/hooks/apis/agent/useCreateReward';
import { useGetReward } from '~/hooks/apis/agent/useGetReward';
import { useUpdateReward } from '~/hooks/apis/agent/useUpdateReward';

import type { Dayjs } from '~/services/TimeService';
import { SYDNEY_TIMEZONE } from '~/services/TimeService';

import type {
  Commission,
  CreateRewardParams,
  Reward,
  RewardTargetAudience,
  RewardType,
} from '~/types/services/agentHub';

import { buildCommissionConditionBreakdown } from '~/utils/agentHub';
import { parseCommissionRuleBoundaryToSydneyDate } from '~/utils/commissionDates';
import { capitalise } from '~/utils/stringUtils';

import {
  type CommissionConditionOperator,
  TEXT_TRANSFORM_NONE,
  createFormId,
} from '../Commission/AgentHubCommissions/CommissionDialog.types';
import CommissionForm, { INPUT_BORDER, SHRUNK_LABEL } from '../Commission/AgentHubCommissions/CommissionForm';
import { buildCommissionPayload } from '../Commission/AgentHubCommissions/commissionFormPayload';
import { useCommissionForm } from '../Commission/AgentHubCommissions/useCommissionForm';

import RewardConditionsEditor from './RewardConditionsEditor';
import RewardPreviewDialog, { type RewardPreviewDetails } from './RewardPreviewDialog';
import {
  REWARD_TARGET_AUDIENCES,
  buildRewardCommissionDescription,
  buildRewardConditionBreakdown,
  createEmptyRewardCondition,
  validateRewardForm,
} from './rewardForm';
import type { RewardConditionForm } from './rewardForm';

interface Props {
  data?: Reward;
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
}

const REWARD_TYPES: Array<RewardType> = ['commission', 'credit', 'lePoints'];

function toCommission(rule: Reward['commissions'][number]): Commission {
  return {
    ...rule,
    commissionGroups: rule.commissionGroups.map((group) => ({
      id: group.id,
      operator: group.operator === 'OR' ? 'OR' : 'AND',
      conditions: group.conditions.map((condition) => ({
        id: condition.id,
        operator: condition.operator as CommissionConditionOperator,
        value: condition.value,
        type: condition.type,
      })),
    })),
  };
}

export default function RewardDialog({ data, isOpen, onClose, mode }: Props) {
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const { enqueueSnackbar } = useSnackbar();
  const { data: fetchedReward } = useGetReward(data?.id, {
    enabled: isEdit && Boolean(data?.id),
  });
  const reward = fetchedReward ?? data;
  const commissionRule = reward?.commissions?.[0];
  const existingCommission = useMemo(
    () => (commissionRule ? toCommission(commissionRule) : undefined),
    [commissionRule],
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rewardType, setRewardType] = useState<RewardType>('commission');
  const [targetAudience, setTargetAudience] = useState<RewardTargetAudience>('agent');
  const [operator, setOperator] = useState<'AND' | 'OR'>('AND');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [expiryDate, setExpiryDate] = useState<Dayjs | null>(null);
  const [conditions, setConditions] = useState<Array<RewardConditionForm>>([
    createEmptyRewardCondition(createFormId()),
  ]);
  const [preview, setPreview] = useState<RewardPreviewDetails | null>(null);
  const [previewPayload, setPreviewPayload] = useState<CreateRewardParams | null>(null);

  const {
    value: commissionValue,
    onChange: setCommissionValue,
    isLoadingConditions,
    addCondition,
    handleChangeCondition,
    handleConditionOperatorChange,
    handleClearCondition,
    handleAppendCondition,
  } = useCommissionForm({
    data: existingCommission,
    isOpen,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!reward) {
      setName('');
      setDescription('');
      setRewardType('commission');
      setTargetAudience('agent');
      setOperator('AND');
      setStartDate(null);
      setEndDate(null);
      setExpiryDate(null);
      setConditions([createEmptyRewardCondition(createFormId())]);
      return;
    }

    setName(reward.name);
    setDescription(reward.description);
    setRewardType(reward.rewardType);
    setTargetAudience(reward.targetAudience);
    setOperator(reward.operator === 'OR' ? 'OR' : 'AND');
    setStartDate(parseCommissionRuleBoundaryToSydneyDate(reward.startDate));
    setEndDate(parseCommissionRuleBoundaryToSydneyDate(reward.endDate));
    setExpiryDate(parseCommissionRuleBoundaryToSydneyDate(reward.expiryDate));
    setConditions(
      reward.conditions.map((condition) => ({
        id: condition.id,
        type: condition.type,
        operator: condition.operator as RewardConditionForm['operator'],
        threshold: condition.threshold,
      })),
    );
  }, [isOpen, reward]);

  useEffect(() => {
    if (!isOpen || !expiryDate) {
      return;
    }
    setCommissionValue((current) => {
      if (current.endDate?.isSame(expiryDate, 'day')) {
        return current;
      }
      return { ...current, endDate: expiryDate };
    });
  }, [expiryDate, isOpen, setCommissionValue]);

  useEffect(() => {
    if (!isOpen || !startDate) {
      return;
    }
    setCommissionValue((current) => {
      if (current.startDate?.isSame(startDate, 'day')) {
        return current;
      }
      return { ...current, startDate };
    });
  }, [isOpen, setCommissionValue, startDate]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setCommissionValue((current) => {
      if (current.commissionType === 'incentive') {
        return current;
      }
      return { ...current, commissionType: 'incentive' };
    });
  }, [isOpen, setCommissionValue]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const nextDescription = buildRewardCommissionDescription(description);
    setCommissionValue((current) => {
      if (current.description === nextDescription) {
        return current;
      }
      return { ...current, description: nextDescription };
    });
  }, [description, isOpen, setCommissionValue]);

  const createRewardMutation = useCreateReward({
    onSuccess: () => {
      enqueueSnackbar('Reward created', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });
  const updateRewardMutation = useUpdateReward({
    onSuccess: () => {
      enqueueSnackbar('Reward updated', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });

  function resetAndClose() {
    setPreview(null);
    setPreviewPayload(null);
    onClose();
  }

  function buildValidatedPayload() {
    let commission;
    if (rewardType === 'commission') {
      const commissionResult = buildCommissionPayload(commissionValue, existingCommission);
      if ('error' in commissionResult) {
        enqueueSnackbar(commissionResult.error, { variant: 'error' });
        return;
      }
      commission = commissionResult.payload;
    }

    const result = validateRewardForm({
      name,
      description,
      rewardType,
      targetAudience,
      operator,
      startDate,
      endDate,
      expiryDate,
      conditions,
      commission,
    });
    if ('error' in result) {
      enqueueSnackbar(result.error, { variant: 'error' });
      return;
    }

    return result.payload;
  }

  function formatSelectedDate(date: Dayjs | null) {
    return date ? date.format('DD/MM/YYYY') : '-';
  }

  function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    handlePreview();
  }

  function handlePreview() {
    const payload = buildValidatedPayload();
    if (!payload) {
      return;
    }

    const valueLimits = Object.entries(commissionValue.commissionValueLimits)
      .filter(([, amount]) => amount.trim() !== '')
      .map(([region, amount]) => `${region} ${amount.trim()}`)
      .join(', ');

    setPreviewPayload(payload);
    setPreview({
      name: payload.name,
      description: payload.description,
      rewardType: capitalise(payload.rewardType),
      targetAudience: capitalise(payload.targetAudience),
      operator: payload.operator,
      startDate: formatSelectedDate(startDate),
      endDate: formatSelectedDate(endDate),
      expiryDate: formatSelectedDate(expiryDate),
      rewardBreakdown: buildRewardConditionBreakdown(conditions).join(` ${operator} `),
      commissionPercentage: commissionValue.commissionPercentage,
      commissionType: capitalise(commissionValue.commissionType),
      commissionDescription: payload.commission?.description ?? '',
      regions: commissionValue.regions.length > 0 ? commissionValue.regions.join(', ') : 'All regions',
      commissionStartDate: formatSelectedDate(commissionValue.startDate),
      commissionEndDate: formatSelectedDate(commissionValue.endDate),
      limit: commissionValue.shouldIncludeLimit ? commissionValue.limit : 'No limit',
      valueLimits,
      includesTaxesAndFees: commissionValue.includesTaxesAndFees ? 'Yes' : 'No',
      includesMargin: commissionValue.includesMargin ? 'Yes' : 'No',
      commissionBreakdown: buildCommissionConditionBreakdown(Object.values(commissionValue.commissionGroups)).join(
        '; ',
      ),
    });
  }

  function handleConfirm() {
    if (!previewPayload) {
      return;
    }

    const onSuccess = () => {
      resetAndClose();
    };

    if (isCreate) {
      createRewardMutation.mutate(previewPayload, { onSuccess });
      return;
    }

    updateRewardMutation.mutate({ rewardId: data?.id, data: previewPayload }, { onSuccess });
  }

  const isSaving = createRewardMutation.isPending || updateRewardMutation.isPending;

  return (
    <>
      <Dialog open={isOpen} fullWidth maxWidth="md" onClose={resetAndClose}>
        <DialogTitle>{isCreate ? 'Create reward' : `Edit reward "${reward?.name}"`}</DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <Stack padding={2} paddingTop={0} direction="column" spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              fullWidth
              InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
              InputProps={INPUT_BORDER}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              required
              fullWidth
              InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
              InputProps={INPUT_BORDER}
            />
            <Stack direction="row" gap={2}>
              <FormControl fullWidth>
                <InputLabel shrink>Reward type</InputLabel>
                <Select label="Reward type" value={rewardType} onChange={(event) => setRewardType(event.target.value)}>
                  {REWARD_TYPES.map((type) => (
                    <MenuItem key={type} value={type} disabled={type !== 'commission'}>
                      {capitalise(type)}
                      {type !== 'commission' ? ' (unsupported)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel shrink>Target audience</InputLabel>
                <Select
                  label="Target audience"
                  value={targetAudience}
                  onChange={(event) => setTargetAudience(event.target.value)}
                >
                  {REWARD_TARGET_AUDIENCES.map((audience) => (
                    <MenuItem key={audience} value={audience}>
                      {capitalise(audience)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel shrink>Condition operator</InputLabel>
                <Select
                  label="Condition operator"
                  value={operator}
                  onChange={(event) => setOperator(event.target.value)}
                >
                  <MenuItem value="AND">AND</MenuItem>
                  <MenuItem value="OR">OR</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" gap={2}>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                format="DD/MM/YYYY"
                label="Start date"
                timezone={SYDNEY_TIMEZONE}
                sx={INPUT_BORDER.sx}
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER, required: true } }}
              />
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                format="DD/MM/YYYY"
                label="End date"
                timezone={SYDNEY_TIMEZONE}
                sx={INPUT_BORDER.sx}
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER, required: true } }}
              />
              <DatePicker
                value={expiryDate}
                onChange={setExpiryDate}
                format="DD/MM/YYYY"
                label="Expiry date"
                timezone={SYDNEY_TIMEZONE}
                sx={INPUT_BORDER.sx}
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER, required: true } }}
              />
            </Stack>
            <RewardConditionsEditor conditions={conditions} operator={operator} onChange={setConditions} />
            {rewardType === 'commission' && (
              <>
                <Divider />
                <Typography variant="button" color="secondary">
                  Commission
                </Typography>
                <CommissionForm
                  value={commissionValue}
                  onChange={setCommissionValue}
                  data={existingCommission}
                  lockedFields={{ startDate: true, endDate: true, commissionType: true, description: true }}
                  hiddenFields={{
                    startDate: true,
                    endDate: true,
                    commissionType: true,
                    description: true,
                  }}
                  isLoadingConditions={isLoadingConditions}
                  addCondition={addCondition}
                  handleChangeCondition={handleChangeCondition}
                  handleConditionOperatorChange={handleConditionOperatorChange}
                  handleClearCondition={handleClearCondition}
                  handleAppendCondition={handleAppendCondition}
                />
              </>
            )}
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button sx={TEXT_TRANSFORM_NONE} type="button" variant="text" onClick={resetAndClose}>
                Cancel
              </Button>
              <Button sx={TEXT_TRANSFORM_NONE} type="submit" variant="contained" color="primary">
                Preview
              </Button>
            </Stack>
          </Stack>
        </form>
      </Dialog>
      <RewardPreviewDialog
        open={preview !== null}
        isCreate={isCreate}
        isSaving={isSaving}
        details={preview}
        onBack={() => setPreview(null)}
        onConfirm={handleConfirm}
      />
    </>
  );
}
